import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Search, Filter, Download, X, ChevronLeft, ChevronRight,
  Users2, DollarSign, ListChecks, CalendarDays, StickyNote,
  Contact2, LayoutTemplate, Settings2, Shield, MoreHorizontal,
  ExternalLink, AlertCircle, Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { auditApi, entitiesApi } from "@/lib/api";
import { toast } from "sonner";

/**
 * AuditPage — admin-only accountability trail.
 *
 * Reuses the canonical `/api/audit` endpoint. Rows are enriched server-side
 * with actor name/email, entity/project name, and category so this page
 * never has to make N follow-up requests. All filters map 1:1 to server
 * query params so the CSV export produces the same set the user sees.
 *
 * Immutability: this UI is READ-ONLY. There is no edit / delete affordance
 * anywhere. Audit rows are append-only history.
 */

const CATEGORY_META = {
  team:       { label: "Team / Permissions",  icon: Users2,         color: "text-sky-300 bg-sky-500/10 border-sky-500/25" },
  financial:  { label: "Financial",           icon: DollarSign,     color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/25" },
  tasks:      { label: "Tasks",               icon: ListChecks,     color: "text-indigo-300 bg-indigo-500/10 border-indigo-500/25" },
  projects:   { label: "Projects",            icon: LayoutTemplate, color: "text-violet-300 bg-violet-500/10 border-violet-500/25" },
  calendar:   { label: "Calendar",            icon: CalendarDays,   color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/25" },
  notes:      { label: "Notes / Decisions",   icon: StickyNote,     color: "text-amber-300 bg-amber-500/10 border-amber-500/25" },
  contacts:   { label: "Contacts",            icon: Contact2,       color: "text-rose-300 bg-rose-500/10 border-rose-500/25" },
  templates:  { label: "Templates",           icon: LayoutTemplate, color: "text-purple-300 bg-purple-500/10 border-purple-500/25" },
  settings:   { label: "Settings",            icon: Settings2,      color: "text-slate-300 bg-slate-500/10 border-slate-500/25" },
  other:      { label: "Other",               icon: Shield,         color: "text-white/60 bg-white/5 border-white/10" },
};

const QUICK_RANGES = [
  { key: "today",  label: "Today",       days: 0 },
  { key: "7d",     label: "Last 7 days", days: 7 },
  { key: "30d",    label: "Last 30 days", days: 30 },
];

const PAGE_SIZE = 50;

export default function AuditPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [actors, setActors] = useState([]);
  const [entities, setEntities] = useState([]);
  const [filters, setFilters] = useState({
    q: "", actor_id: "", category: "", entity_id: "", date_from: "", date_to: "",
  });

  const activeParams = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) out[k] = v; });
    return out;
  }, [filters]);

  useEffect(() => {
    Promise.all([
      auditApi.actors().then((r) => setActors(r.data || [])).catch(() => {}),
      entitiesApi.list().then((r) => setEntities(r.data || [])).catch(() => {}),
    ]);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    auditApi.list({ ...activeParams, limit: PAGE_SIZE, offset })
      .then((r) => {
        if (cancelled) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403 || err?.response?.status === 401) {
          toast.error("You do not have access to the audit log");
        } else {
          toast.error("Failed to load audit log");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeParams, offset]);

  const setFilter = (patch) => {
    setOffset(0);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const applyQuickRange = (days) => {
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - days);
    setFilter({
      date_from: from.toISOString().slice(0, 10),
      date_to: to.toISOString().slice(0, 10),
    });
  };

  const clearFilters = () => {
    setOffset(0);
    setFilters({ q: "", actor_id: "", category: "", entity_id: "", date_from: "", date_to: "" });
  };

  const hasActive = Object.values(filters).some(Boolean);
  const nextOffset = offset + PAGE_SIZE;
  const prevOffset = Math.max(0, offset - PAGE_SIZE);
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(total, offset + items.length);

  const download = async () => {
    try {
      const blob = await auditApi.exportCsv(activeParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="h-full overflow-y-auto" data-testid="audit-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10 pb-24 md:pb-16">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-eyebrow mb-1">Accountability</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Audit log</h1>
            <p className="text-white/50 mt-2 text-sm max-w-2xl">
              Every important write in HQ — who did what, when, where. Append-only history; rows cannot be edited or deleted.
            </p>
          </div>
          <Button
            onClick={download}
            data-testid="audit-export-csv"
            className="rounded-full bg-white text-black hover:bg-white/90"
          >
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] mb-4 overflow-hidden">
          <div className="p-3 md:p-4 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={filters.q}
                onChange={(e) => setFilter({ q: e.target.value })}
                placeholder="Search actor, action, record…"
                data-testid="audit-search-input"
                className="pl-9 bg-white/5 border-white/10 text-white h-9"
              />
            </div>
            <FilterSelect
              value={filters.category}
              onChange={(v) => setFilter({ category: v })}
              placeholder="All categories"
              testid="audit-category-filter"
              options={[
                { value: "", label: "All categories" },
                ...Object.entries(CATEGORY_META).map(([k, v]) => ({ value: k, label: v.label })),
              ]}
            />
            <FilterSelect
              value={filters.actor_id}
              onChange={(v) => setFilter({ actor_id: v })}
              placeholder="All actors"
              testid="audit-actor-filter"
              options={[
                { value: "", label: "All actors" },
                ...actors.map((a) => ({ value: a.id, label: a.name })),
              ]}
            />
            <FilterSelect
              value={filters.entity_id}
              onChange={(v) => setFilter({ entity_id: v })}
              placeholder="All entities"
              testid="audit-entity-filter"
              options={[
                { value: "", label: "All entities" },
                ...(entities || []).map((e) => ({ value: e.id, label: e.name })),
              ]}
            />
            <div className="flex items-center gap-1.5 flex-wrap">
              {QUICK_RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => applyQuickRange(r.days)}
                  data-testid={`audit-quick-${r.key}`}
                  className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors"
                >
                  {r.label}
                </button>
              ))}
              {hasActive && (
                <button
                  onClick={clearFilters}
                  data-testid="audit-clear-filters"
                  className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/[0.08] transition-colors inline-flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
          </div>
          <div className="px-3 md:px-4 pb-3 md:pb-4 grid grid-cols-2 gap-2 max-w-md">
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilter({ date_from: e.target.value })}
              data-testid="audit-date-from"
              className="bg-white/5 border-white/10 text-white h-9"
            />
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilter({ date_to: e.target.value })}
              data-testid="audit-date-to"
              className="bg-white/5 border-white/10 text-white h-9"
            />
          </div>
        </div>

        {/* Rows */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
          {loading && (
            <div className="p-8 text-center text-white/40 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-white/40 text-sm" data-testid="audit-empty">
              No audit entries match your filters.
            </div>
          )}
          {!loading && items.length > 0 && (
            <ul className="divide-y divide-white/5" data-testid="audit-list">
              {items.map((row) => (
                <AuditRow key={row.id} row={row} onOpen={() => setSelected(row)} />
              ))}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="mt-4 flex items-center justify-between gap-2 flex-wrap text-xs text-white/50">
            <div data-testid="audit-pagination-status">
              Showing {showingFrom}–{showingTo} of {total}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setOffset(prevOffset)}
                disabled={offset === 0 || loading}
                data-testid="audit-prev-page"
                className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3 h-3" /> Previous
              </button>
              <button
                onClick={() => setOffset(nextOffset)}
                disabled={nextOffset >= total || loading}
                data-testid="audit-next-page"
                className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      <AuditDetailDrawer row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function AuditRow({ row, onOpen }) {
  const cat = CATEGORY_META[row.category] || CATEGORY_META.other;
  const CatIcon = cat.icon;
  const summary = summarizeRow(row);
  const ts = formatTs(row.at);
  return (
    <li
      className="p-3 md:p-4 hover:bg-white/[0.04] cursor-pointer transition-colors"
      onClick={onOpen}
      data-testid={`audit-row-${row.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-md border flex items-center justify-center flex-shrink-0 ${cat.color}`}>
          <CatIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wider text-white/40">{ts}</span>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${cat.color}`}>{cat.label}</span>
          </div>
          <div className="text-sm mt-1 leading-snug">
            <span className="text-white/85 font-medium">{row.actor_name}</span>
            <span className="text-white/50"> · </span>
            <span className="text-white">{humanAction(row.action)}</span>
            {summary && <span className="text-white/60"> · {summary}</span>}
          </div>
          {(row.entity_name || row.project_name) && (
            <div className="text-[11px] text-white/40 mt-0.5 truncate">
              {row.entity_name}{row.entity_name && row.project_name ? " → " : ""}{row.project_name}
            </div>
          )}
        </div>
        <MoreHorizontal className="w-4 h-4 text-white/30 mt-1 flex-shrink-0" />
      </div>
    </li>
  );
}

function AuditDetailDrawer({ row, onClose }) {
  if (!row) return null;
  const cat = CATEGORY_META[row.category] || CATEGORY_META.other;
  const meta = row.meta || {};
  const changes = meta.changes || null;
  const link = deepLinkFor(row);
  const historicalHint = !Object.keys(meta).some((k) => k !== "email" && k !== "project_id" && k !== "entity_id");
  return (
    <Sheet open={!!row} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg bg-[#0F131B] text-white border-l border-white/10 p-0 flex flex-col"
        data-testid="audit-detail-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/5">
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider w-fit ${cat.color}`}>
            <cat.icon className="w-3 h-3" /> {cat.label}
          </div>
          <SheetTitle className="font-display text-xl mt-2">{humanAction(row.action)}</SheetTitle>
          <SheetDescription className="text-white/50">
            {formatTs(row.at, true)}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-5 text-sm">
          <DetailPair label="Actor" value={<><span className="text-white/90">{row.actor_name}</span>{row.actor_email && <span className="text-white/40 block text-xs">{row.actor_email}</span>}</>} />
          {(row.entity_name || row.project_name) && (
            <DetailPair
              label="Location"
              value={<span>{row.entity_name || "—"}{row.project_name && <> → <span className="text-white/90">{row.project_name}</span></>}</span>}
            />
          )}
          {row.subject_type && row.subject_id && (
            <DetailPair label="Target" value={`${row.subject_type} · ${row.subject_id}`} />
          )}
          {meta.amount != null && meta.currency && (
            <DetailPair label="Amount" value={`${meta.currency} ${meta.amount}`} />
          )}
          {(meta.vendor || meta.source_client) && (
            <DetailPair label={meta.source_client ? "Client" : "Vendor"} value={meta.vendor || meta.source_client} />
          )}
          {(meta.title || meta.name || meta.description || meta.full_name) && (
            <DetailPair label="Record" value={meta.title || meta.name || meta.full_name || meta.description} />
          )}
          {changes && Object.keys(changes).length > 0 && (
            <div>
              <div className="text-eyebrow mb-2">Changes</div>
              <div className="rounded-md border border-white/10 bg-white/[0.03] divide-y divide-white/5">
                {Object.entries(changes).map(([k, v]) => (
                  <div key={k} className="px-3 py-2 text-xs" data-testid={`audit-detail-change-${k}`}>
                    <div className="text-white/40 uppercase tracking-wider text-[10px]">{k}</div>
                    <div className="mt-1 text-white/85 break-all">
                      <span className="text-white/50">{stringifyValue(v?.from)}</span>
                      <span className="text-white/30"> → </span>
                      <span className="text-white">{stringifyValue(v?.to)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {historicalHint && (
            <div className="text-[11px] text-white/40 border border-white/10 rounded-md p-3 bg-white/[0.02] flex items-start gap-2" data-testid="audit-historical-hint">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>Historical audit — limited context available. New activity captures richer detail.</span>
            </div>
          )}
          {link && (
            <div className="pt-2">
              <Link
                to={link}
                data-testid="audit-deep-link"
                className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white underline underline-offset-4 decoration-white/20 hover:decoration-white/60"
              >
                Open canonical record <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailPair({ label, value }) {
  return (
    <div>
      <div className="text-eyebrow">{label}</div>
      <div className="mt-1 text-sm text-white/85">{value || "—"}</div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
      <SelectTrigger className="w-[160px] bg-white/5 border-white/10 text-white h-9" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#151A23] border-white/10 text-white max-h-[300px]">
        {options.map((o) => (
          <SelectItem key={o.value || "__all__"} value={o.value || "__all__"} className="text-white focus:bg-white/10">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function stringifyValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function humanAction(action) {
  if (!action) return "Unknown action";
  return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function summarizeRow(row) {
  const m = row.meta || {};
  if (m.amount != null && m.currency) {
    const who = m.vendor || m.source_client;
    return `${m.currency} ${m.amount}${who ? ` · ${who}` : ""}`;
  }
  if (m.title) return m.title;
  if (m.name) return m.name;
  if (m.full_name) return m.full_name;
  if (m.email) return m.email;
  if (m.description) return m.description.slice(0, 80);
  return null;
}

function deepLinkFor(row) {
  const m = row.meta || {};
  if (!row.subject_id) return null;
  const st = row.subject_type;
  if (st === "task" && m.entity_id)      return `/entities/${m.entity_id}?tab=tasks&focus=${row.subject_id}`;
  if (st === "note" && m.entity_id)      return `/entities/${m.entity_id}?tab=notes&focus=${row.subject_id}`;
  if (st === "event" && m.entity_id)     return `/entities/${m.entity_id}?tab=calendar&focus=${row.subject_id}`;
  if (st === "expenditure" && m.entity_id) return `/entities/${m.entity_id}?tab=budget&focus=${row.subject_id}`;
  if (st === "recurring" && m.entity_id) return `/entities/${m.entity_id}?tab=budget&focus=${row.subject_id}`;
  if (st === "project" && m.entity_id)   return `/entities/${m.entity_id}?tab=projects&focus=${row.subject_id}`;
  if (st === "contact")                  return `/contacts?focus=${row.subject_id}`;
  if (st === "entity")                   return `/entities/${row.subject_id}`;
  if (st === "membership" || st === "invitation" || st === "user") return `/team`;
  return null;
}

function formatTs(iso, full = false) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (full) return format(d, "PPpp");
    return format(d, "MMM d, p");
  } catch {
    return iso;
  }
}
